import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createMoisture,
  getMoistureByPlant,
  updateMoisture,
} from '../services/MoistureCorrectionServices';
import { displayError } from '../helpers';
import { applyCorrectionFactors } from '../forms/qualitycontrol/MoistureCorrectionUtils';

const MATERIAL_KEYS = ['20mm', '12mm', 'rsand', 'msand', 'csand'];
const MATERIAL_LABELS = {
  '20mm': '20MM',
  '12mm': '12MM',
  rsand: 'RSAND / SAND',
  msand: 'MSAND',
  csand: 'CSAND',
};
const MOISTURE_MAX = 15;
const ABSORPTION_MAX = 5;

const BIN_MATERIALS = [
  { key: '20mm', aggField: 'agg1', binNumber: 1, label: 'Bin 1 / 20MM' },
  { key: '12mm', aggField: 'agg2', binNumber: 2, label: 'Bin 2 / 12MM' },
  { key: 'rsand', aggField: 'agg3', binNumber: 3, label: 'Bin 3 / RSAND' },
  { key: 'msand', aggField: 'agg4', binNumber: 4, label: 'Bin 4 / MSAND' },
  { key: 'csand', aggField: 'agg5', binNumber: 5, label: 'Bin 5 / CSAND' },
];

const EMPTY_PCT_MAP = () => (
  MATERIAL_KEYS.reduce((acc, key) => {
    acc[key] = '0.00';
    return acc;
  }, {})
);

function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function formatPct(value) {
  return toNumber(value).toFixed(2);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, toNumber(value)));
}

/** Match moisture master key from plant bin label (e.g. both bins named 20MM → 20mm). */
function moistureKeyFromBinLabel(label, fallbackKey) {
  const normalized = String(label || '')
    .toLowerCase()
    .replace(/[\s_\-./]/g, '');
  if (!normalized) {
    return fallbackKey;
  }
  if (normalized.includes('20mm') || normalized === '20' || normalized === '20m') {
    return '20mm';
  }
  if (normalized.includes('12mm') || normalized === '12' || normalized === '12m') {
    return '12mm';
  }
  if (normalized.includes('msand') || normalized.includes('manufacturedsand')) {
    return 'msand';
  }
  if (normalized.includes('csand') || normalized.includes('crushersand')) {
    return 'csand';
  }
  if (
    normalized.includes('rsand')
    || normalized.includes('riversand')
    || normalized === 'sand'
  ) {
    return 'rsand';
  }
  return fallbackKey;
}

function mapsFromApiResponse(response) {
  const moisture = EMPTY_PCT_MAP();
  const absorption = EMPTY_PCT_MAP();
  MATERIAL_KEYS.forEach((key) => {
    // Clamp legacy out-of-range DB values so edits can save successfully.
    moisture[key] = formatPct(clamp(response?.moisture?.[key], 0, MOISTURE_MAX));
    absorption[key] = formatPct(clamp(response?.absorption?.[key], 0, ABSORPTION_MAX));
  });
  return {
    id: response?.id || null,
    moisture,
    absorption,
  };
}

function validatePctMaps(moistureMap, absorptionMap) {
  const invalid = [];
  MATERIAL_KEYS.forEach((key) => {
    const label = MATERIAL_LABELS[key] || key;
    const moi = toNumber(moistureMap[key]);
    const abs = toNumber(absorptionMap[key]);
    if (moi < 0 || moi > MOISTURE_MAX) {
      invalid.push(`${label} moisture must be 0–${MOISTURE_MAX}% (got ${moi})`);
    }
    if (abs < 0 || abs > ABSORPTION_MAX) {
      invalid.push(`${label} absorption must be 0–${ABSORPTION_MAX}% (got ${abs})`);
    }
  });
  return invalid;
}

function buildMoisturePayload(plantId, recordId, moistureMap, absorptionMap) {
  const fields = applyCorrectionFactors({
    id: recordId,
    plant_id: parseInt(plantId, 10),
    moisture_msand: clamp(moistureMap.msand, 0, MOISTURE_MAX),
    moisture_csand: clamp(moistureMap.csand, 0, MOISTURE_MAX),
    moisture_rsand: clamp(moistureMap.rsand, 0, MOISTURE_MAX),
    moisture_12mm: clamp(moistureMap['12mm'], 0, MOISTURE_MAX),
    moisture_20mm: clamp(moistureMap['20mm'], 0, MOISTURE_MAX),
    absorption_msand: clamp(absorptionMap.msand, 0, ABSORPTION_MAX),
    absorption_csand: clamp(absorptionMap.csand, 0, ABSORPTION_MAX),
    absorption_rsand: clamp(absorptionMap.rsand, 0, ABSORPTION_MAX),
    absorption_12mm: clamp(absorptionMap['12mm'], 0, ABSORPTION_MAX),
    absorption_20mm: clamp(absorptionMap['20mm'], 0, ABSORPTION_MAX),
  });
  return {
    id: recordId,
    plant_id: fields.plant_id,
    plant: fields.plant_id,
    moisture_msand: fields.moisture_msand,
    moisture_csand: fields.moisture_csand,
    moisture_rsand: fields.moisture_rsand,
    moisture_12mm: fields.moisture_12mm,
    moisture_20mm: fields.moisture_20mm,
    absorption_msand: fields.absorption_msand,
    absorption_csand: fields.absorption_csand,
    absorption_rsand: fields.absorption_rsand,
    absorption_12mm: fields.absorption_12mm,
    absorption_20mm: fields.absorption_20mm,
    correction_factor_msand: toNumber(fields.correction_factor_msand),
    correction_factor_csand: toNumber(fields.correction_factor_csand),
    correction_factor_rsand: toNumber(fields.correction_factor_rsand),
    correction_factor_12mm: toNumber(fields.correction_factor_12mm),
    correction_factor_20mm: toNumber(fields.correction_factor_20mm),
  };
}

/**
 * Plant moisture / CF / target preview with editable Moisture % and Absorption %.
 * Edits save to the plant Moisture Correction master (create or update).
 */
function DesignMixMoistureTargetPanel({ cookies, plantId, inputs, plantConfig }) {
  const [recordId, setRecordId] = useState(null);
  const [moistureMap, setMoistureMap] = useState(EMPTY_PCT_MAP);
  const [absorptionMap, setAbsorptionMap] = useState(EMPTY_PCT_MAP);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const latestRef = useRef({ recordId: null, moistureMap: EMPTY_PCT_MAP(), absorptionMap: EMPTY_PCT_MAP() });

  useEffect(() => {
    latestRef.current = { recordId, moistureMap, absorptionMap };
  }, [recordId, moistureMap, absorptionMap]);

  useEffect(() => {
    if (!plantId) {
      setRecordId(null);
      setMoistureMap(EMPTY_PCT_MAP());
      setAbsorptionMap(EMPTY_PCT_MAP());
      setSaveStatus('');
      return undefined;
    }
    let cancelled = false;
    getMoistureByPlant(cookies, plantId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const mapped = mapsFromApiResponse(response);
        setRecordId(mapped.id);
        setMoistureMap(mapped.moisture);
        setAbsorptionMap(mapped.absorption);
        latestRef.current = {
          recordId: mapped.id,
          moistureMap: mapped.moisture,
          absorptionMap: mapped.absorption,
        };
        setSaveStatus('');
      })
      .catch(() => {
        if (!cancelled) {
          const emptyMoi = EMPTY_PCT_MAP();
          const emptyAbs = EMPTY_PCT_MAP();
          setRecordId(null);
          setMoistureMap(emptyMoi);
          setAbsorptionMap(emptyAbs);
          latestRef.current = { recordId: null, moistureMap: emptyMoi, absorptionMap: emptyAbs };
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cookies, plantId]);

  const persistMoisture = () => {
    if (!plantId || saving) {
      return;
    }
    const { recordId: id, moistureMap: moi, absorptionMap: abs } = latestRef.current;

    // Normalize + clamp before send (also fixes legacy out-of-range keys not shown in the table).
    const normalizedMoi = { ...moi };
    const normalizedAbs = { ...abs };
    MATERIAL_KEYS.forEach((key) => {
      normalizedMoi[key] = formatPct(clamp(moi[key], 0, MOISTURE_MAX));
      normalizedAbs[key] = formatPct(clamp(abs[key], 0, ABSORPTION_MAX));
    });
    setMoistureMap(normalizedMoi);
    setAbsorptionMap(normalizedAbs);
    latestRef.current = { recordId: id, moistureMap: normalizedMoi, absorptionMap: normalizedAbs };

    const invalid = validatePctMaps(normalizedMoi, normalizedAbs);
    if (invalid.length) {
      setSaveStatus('Save failed');
      displayError(invalid.join('\n'), 'Invalid moisture / absorption values');
      return;
    }

    const payload = buildMoisturePayload(plantId, id, normalizedMoi, normalizedAbs);
    // Create payload must not send null id
    if (!id) {
      delete payload.id;
    }

    setSaving(true);
    setSaveStatus('Saving…');
    const savePromise = id
      ? updateMoisture(cookies, payload, id)
      : createMoisture(cookies, payload);
    savePromise
      .then((response) => {
        if (response?.id) {
          setRecordId(response.id);
        }
        if (response?.moisture || response?.absorption) {
          const mapped = mapsFromApiResponse(response);
          setRecordId(mapped.id);
          setMoistureMap(mapped.moisture);
          setAbsorptionMap(mapped.absorption);
          latestRef.current = {
            recordId: mapped.id,
            moistureMap: mapped.moisture,
            absorptionMap: mapped.absorption,
          };
        }
        setSaveStatus('Saved moisture correction');
      })
      .catch((err) => {
        setSaveStatus('Save failed');
        displayError(err.response?.data ?? err.message, 'Failed to update plant moisture correction');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handlePctChange = (moistureKey, field, rawValue) => {
    const { recordId: id, moistureMap: moi, absorptionMap: abs } = latestRef.current;
    const nextMoi = field === 'moisture'
      ? { ...moi, [moistureKey]: rawValue }
      : moi;
    const nextAbs = field === 'absorption'
      ? { ...abs, [moistureKey]: rawValue }
      : abs;
    if (field === 'moisture') {
      setMoistureMap(nextMoi);
    } else {
      setAbsorptionMap(nextAbs);
    }
    latestRef.current = { recordId: id, moistureMap: nextMoi, absorptionMap: nextAbs };
    setSaveStatus('Editing…');
  };

  const handlePctBlur = (moistureKey, field) => {
    const { recordId: id, moistureMap: moi, absorptionMap: abs } = latestRef.current;
    const max = field === 'moisture' ? MOISTURE_MAX : ABSORPTION_MAX;
    const map = field === 'moisture' ? moi : abs;
    const normalized = formatPct(clamp(map[moistureKey], 0, max));
    const nextMoi = field === 'moisture'
      ? { ...moi, [moistureKey]: normalized }
      : moi;
    const nextAbs = field === 'absorption'
      ? { ...abs, [moistureKey]: normalized }
      : abs;
    if (field === 'moisture') {
      setMoistureMap(nextMoi);
    } else {
      setAbsorptionMap(nextAbs);
    }
    latestRef.current = { recordId: id, moistureMap: nextMoi, absorptionMap: nextAbs };
    persistMoisture();
  };

  const { rows, waterRows, cumulativeCorrectionKg } = useMemo(() => {
    let cumulativeKg = 0;
    const aggregateRows = BIN_MATERIALS.map((material) => {
      const binLabel =
        plantConfig?.[`is_bin_${material.binNumber}`] && plantConfig?.[`bin_${material.binNumber}`]
          ? plantConfig[`bin_${material.binNumber}`]
          : material.label;
      const moistureKey = moistureKeyFromBinLabel(binLabel, material.key);
      const moisturePct = toNumber(moistureMap[moistureKey]);
      const absorptionPct = toNumber(absorptionMap[moistureKey]);
      // CF% = Moisture − Absorption (e.g. moi 5 → CF 5). Target = Recipe + CF(Kg).
      const cfPct = moisturePct - absorptionPct;
      const recipe = toNumber(inputs?.[material.aggField]);
      const correctionKg = Math.round((recipe * cfPct) / 100);
      cumulativeKg += correctionKg;
      const targetPerM3 = Math.max(recipe + correctionKg, 0);
      return {
        key: `${material.aggField}-${moistureKey}`,
        moistureKey,
        label: binLabel || material.label,
        moisturePct,
        absorptionPct,
        cfPct: formatPct(cfPct),
        recipe,
        correctionKg,
        targetPerM3,
      };
    });

    const water1Recipe = toNumber(inputs?.water1);
    const water2Recipe = toNumber(inputs?.water2);
    const water1Label = plantConfig?.is_other_1 && plantConfig?.other_1
      ? plantConfig.other_1
      : 'Water 1';
    const water2Label = plantConfig?.is_other_2 && plantConfig?.other_2
      ? plantConfig.other_2
      : 'Water 2';

    // Free moisture in aggregates reduces batch water by the same CF(Kg) total.
    const waterAdjKg = -cumulativeKg;
    const nextWaterRows = [
      {
        key: 'water1',
        label: water1Label,
        recipe: water1Recipe,
        adjusted: water1Recipe > 0 ? water1Recipe + waterAdjKg : water1Recipe,
      },
      {
        key: 'water2',
        label: water2Label,
        recipe: water2Recipe,
        adjusted: water2Recipe > 0 ? water2Recipe + waterAdjKg : water2Recipe,
      },
    ];

    return {
      rows: aggregateRows,
      waterRows: nextWaterRows,
      cumulativeCorrectionKg: waterAdjKg,
    };
  }, [inputs, moistureMap, absorptionMap, plantConfig]);

  if (!plantId) {
    return null;
  }

  const inputStyle = {
    width: '72px',
    textAlign: 'right',
    display: 'inline-block',
    margin: '0 auto',
  };

  const blockWheel = (event) => {
    event.currentTarget.blur();
  };

  return (
    <div className="form-row table-bordered shadow p-2 my-2 border-secondary bg-blue form-control-panel">
      <h6 className="col-sm-12 mb-2">
        <b>Moisture / Correction Factor / Target (kg/m³)</b>
        <span className="text-muted ml-2" style={{ fontSize: '12px', fontWeight: 400 }}>
          CF% = Moisture − Absorption. Target = Recipe + CF(Kg). Moisture 0–{MOISTURE_MAX}% · Absorption 0–{ABSORPTION_MAX}%.
        </span>
        {saveStatus && (
          <span
            className={`ml-2 ${saveStatus.includes('fail') ? 'text-danger' : 'text-success'}`}
            style={{ fontSize: '12px', fontWeight: 400 }}
          >
            {saving ? 'Saving…' : saveStatus}
          </span>
        )}
      </h6>
      <div className="table-responsive col-sm-12">
        <table className="table table-sm table-bordered mb-0 bg-white">
          <thead>
            <tr>
              <th>Material</th>
              <th className="text-right">Recipe</th>
              <th className="text-center">Moisture %</th>
              <th className="text-center">Absorption %</th>
              <th className="text-right">CF %</th>
              <th className="text-right">CF (Kg)</th>
              <th className="text-right">Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="text-right">{row.recipe}</td>
                <td className="text-center">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={MOISTURE_MAX}
                    className="form-control form-control-sm"
                    style={inputStyle}
                    value={moistureMap[row.moistureKey] ?? '0.00'}
                    onChange={(e) => handlePctChange(row.moistureKey, 'moisture', e.target.value)}
                    onBlur={() => handlePctBlur(row.moistureKey, 'moisture')}
                    onWheel={blockWheel}
                    disabled={saving}
                  />
                </td>
                <td className="text-center">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={ABSORPTION_MAX}
                    className="form-control form-control-sm"
                    style={inputStyle}
                    value={absorptionMap[row.moistureKey] ?? '0.00'}
                    onChange={(e) => handlePctChange(row.moistureKey, 'absorption', e.target.value)}
                    onBlur={() => handlePctBlur(row.moistureKey, 'absorption')}
                    onWheel={blockWheel}
                    disabled={saving}
                  />
                </td>
                <td className="text-right">{row.cfPct}</td>
                <td className="text-right">{row.correctionKg}</td>
                <td className="text-right"><b>{row.targetPerM3}</b></td>
              </tr>
            ))}
          </tbody>
        </table>

        <table className="table table-sm table-bordered mt-2 mb-0 bg-white">
          <thead>
            <tr>
              <th>Water</th>
              <th className="text-right">Recipe (kg/m³)</th>
              <th className="text-right">Agg. moisture kg</th>
              <th className="text-right">Adjusted water (kg/m³)</th>
            </tr>
          </thead>
          <tbody>
            {waterRows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="text-right">{row.recipe}</td>
                <td className="text-right">{row.recipe > 0 ? cumulativeCorrectionKg : 0}</td>
                <td className="text-right"><b>{row.adjusted}</b></td>
              </tr>
            ))}
          </tbody>
        </table>

        {!recordId && (
          <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
            No moisture correction yet for this plant. Leaving a field will create it.
          </div>
        )}
      </div>
    </div>
  );
}

export default DesignMixMoistureTargetPanel;
